import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  ContentChild,
  Input,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { TranslatePipe } from '@next/core-lib/i18n';
import { JoinPipe } from '@next/core-lib/pipes';
import { Subscription } from 'rxjs';

import { FileUploadErrorsTemplateDirective } from '../directives/file-upload-errors-template.directive';
import { FileUploadPreviewTemplateDirective } from '../directives/file-upload-preview-template.directive';
import { FileTransformerInterface } from '../models/file-transformer.interface';

import { HumanFileSizePipe } from '../pipes/human-file-size.pipe';
import { ObjectUrlPipe } from '../pipes/object-url.pipe';
import { maxFileSize } from '../validators/max-file-size.validator';

interface FileLike {
  size: number;
}
type UploadValue = File | FileLike | null;

@Component({
  selector: 'agr-file-upload',
  templateUrl: 'file-upload.component.html',
  styleUrls: ['file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FileUploadComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: FileUploadComponent,
    },
  ],
  imports: [
    MatButton,
    MatIcon,
    NgTemplateOutlet,
    MatDivider,
    TranslatePipe,
    JoinPipe,
    HumanFileSizePipe,
    ObjectUrlPipe,
  ],
})
export class FileUploadComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  readonly chooseFileTextValue = signal<string | null>(null);
  readonly chooseOtherFileTextValue = signal<string | null>(null);
  readonly resetTextValue = signal<string | null>(null);
  readonly downloadTextValue = signal<string | null>(null);
  readonly rechooseEnabledValue = signal(true);
  readonly resetEnabledValue = signal(true);
  readonly downloadEnabledValue = signal(true);
  readonly chooseIconValue = signal('attachment');
  readonly rechooseIconValue = signal('link');
  readonly resetIconValue = signal('delete_outline');
  readonly downloadIconValue = signal('file_download');

  @Input()
  set chooseFileText(value: string | null) {
    this.chooseFileTextValue.set(value);
  }

  @Input()
  set chooseOtherFileText(value: string | null) {
    this.chooseOtherFileTextValue.set(value);
  }

  @Input()
  set resetText(value: string | null) {
    this.resetTextValue.set(value);
  }

  @Input()
  set downloadText(value: string | null) {
    this.downloadTextValue.set(value);
  }

  @Input() allowedMimeTypes: string[] = ['image/png', 'image/jpeg'];

  @Input()
  set rechooseEnabled(value: boolean) {
    this.rechooseEnabledValue.set(value);
  }

  @Input()
  set resetEnabled(value: boolean) {
    this.resetEnabledValue.set(value);
  }

  @Input()
  set downloadEnabled(value: boolean) {
    this.downloadEnabledValue.set(value);
  }

  @Input()
  set chooseIcon(value: string) {
    this.chooseIconValue.set(value);
  }

  @Input()
  set rechooseIcon(value: string) {
    this.rechooseIconValue.set(value);
  }

  @Input()
  set resetIcon(value: string) {
    this.resetIconValue.set(value);
  }

  @Input()
  set downloadIcon(value: string) {
    this.downloadIconValue.set(value);
  }

  @Input() transformer: FileTransformerInterface | null;

  @ContentChild(FileUploadPreviewTemplateDirective, { read: TemplateRef })
  previewTemplate: TemplateRef<unknown>;
  @ContentChild(FileUploadErrorsTemplateDirective, { read: TemplateRef })
  errorTemplate: TemplateRef<unknown>;

  control = new FormControl<UploadValue>(null);
  readonly maxFileSizeValue = signal<number | null>(null); // In bytes
  readonly controlValue = signal<UploadValue>(null);
  readonly controlErrors = signal<ValidationErrors | null>(null);
  readonly hasFile = computed(() => this.controlValue() !== null);
  readonly fileForObjectUrl = computed(() => {
    const value = this.controlValue();
    return value instanceof File ? value : null;
  });

  private subscriptions: Subscription[] = [];

  @Input()
  set maxFileSize(sizeInBytes: number | null) {
    if (sizeInBytes) {
      this.control.addValidators(maxFileSize(sizeInBytes));
    } else {
      this.control.setValidators([]);
    }
    this.maxFileSizeValue.set(sizeInBytes);
    this.control.updateValueAndValidity({ emitEvent: false });
    this.syncControlState();
  }

  onChange = (_: UploadValue) => {};
  onTouched = () => {};

  ngOnInit(): void {
    this.subscriptions.push(
      this.control.valueChanges.subscribe((it) => {
        this.syncControlState();
        this.onChange(it);
      }),
      this.control.statusChanges.subscribe(() => this.syncControlState()),
    );
    this.syncControlState();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  registerOnChange(fn: (_: UploadValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: unknown): void {
    const fileValue: UploadValue = isUploadValue(value) ? value : null;
    this.control.setValue(fileValue, { emitEvent: false });
    this.control.updateValueAndValidity({ emitEvent: false });
    this.syncControlState();
  }

  async onFileUploadChange(input: HTMLInputElement) {
    const file = input.files.item(0);
    if (!file) {
      this.control.setValue(null);
      return;
    }

    if (this.transformer) {
      const transformedFile = await this.transformer.transform(file);
      this.control.setValue(transformedFile);
    } else {
      this.control.setValue(file);
    }
  }

  reset(): void {
    this.control.reset();
    this.syncControlState();
  }

  validate(): ValidationErrors | null {
    return this.control.errors;
  }

  private syncControlState(): void {
    this.controlValue.set(this.control.value);
    this.controlErrors.set(this.control.errors);
  }
}

const isUploadValue = (value: unknown): value is UploadValue => {
  if (value === null || value instanceof File) {
    return true;
  }
  if (typeof value === 'object' && value !== null && 'size' in value) {
    return typeof value.size === 'number';
  }
  return false;
};
